import {claimPlayback} from './exclusivePlayback';

// Call recordings are stereo with one party per channel, so a listener with a
// single earbud only hears one side. Playback is routed through Web Audio and
// summed to mono; the file itself (and the download) stays stereo.
//
// L and R are summed at full level, so each speaker keeps the loudness they had
// in their own channel, and a soft clipper catches the peaks when both talk at once.

const SOFT_CLIP_THRESHOLD = 0.8;
const SOFT_CLIP_CURVE_SIZE = 2048;
// The summed signal can reach ±2; it is halved before the shaper (whose input
// domain is ±1) and the curve doubles it back.
const PRE_SHAPER_GAIN = 0.5;

type AudioContextCtor = new () => AudioContext;

let sharedContext: AudioContext | null = null;
const connectedElements = new WeakSet<HTMLMediaElement>();

/** Curve for a WaveShaperNode fed with (L+R)/2: identity on L+R below the threshold, smooth saturation to ±1 above it. */
export function buildSoftClipCurve(size = SOFT_CLIP_CURVE_SIZE, threshold = SOFT_CLIP_THRESHOLD): Float32Array {
    const curve = new Float32Array(size);
    for (let i = 0; i < size; i++) {
        const x = (i / (size - 1)) * 2 - 1;
        const signal = x / PRE_SHAPER_GAIN;
        const magnitude = Math.abs(signal);
        const shaped = magnitude <= threshold
            ? magnitude
            : threshold + (1 - threshold) * Math.tanh((magnitude - threshold) / (1 - threshold));
        curve[i] = Math.sign(signal) * shaped;
    }
    return curve;
}

function getAudioContext(): AudioContext | null {
    if (sharedContext) return sharedContext;
    const Ctor: AudioContextCtor | undefined =
        window.AudioContext ?? (window as unknown as {webkitAudioContext?: AudioContextCtor}).webkitAudioContext;
    if (!Ctor) return null;
    sharedContext = new Ctor();
    return sharedContext;
}

/** Route an audio element through a mono downmix. Idempotent per element; no-op without Web Audio support. */
export function connectMono(audio: HTMLMediaElement): AudioContext | null {
    const context = getAudioContext();
    if (!context) return null;
    if (connectedElements.has(audio)) return context;

    const source = context.createMediaElementSource(audio);
    const splitter = context.createChannelSplitter(2);

    const mono = context.createGain();
    mono.channelCount = 1;
    mono.channelCountMode = 'explicit';
    mono.channelInterpretation = 'discrete';
    mono.gain.value = PRE_SHAPER_GAIN;

    const softClip = context.createWaveShaper();
    softClip.curve = buildSoftClipCurve();
    softClip.oversample = '2x';

    source.connect(splitter);
    // Both splitter outputs feed the same mono input, where they are summed.
    splitter.connect(mono, 0);
    splitter.connect(mono, 1);
    mono.connect(softClip);
    // The destination up-mixes the mono signal to both speakers.
    softClip.connect(context.destination);

    connectedElements.add(audio);
    return context;
}

/** Play an audio element in mono. Falls back to normal (stereo) playback if Web Audio is unavailable. */
export async function playMono(audio: HTMLMediaElement): Promise<void> {
    try {
        const context = connectMono(audio);
        if (context && context.state === 'suspended') {
            await context.resume();
        }
    } catch (error) {
        console.warn('Mono playback unavailable, playing the recording as is', error);
    }
    claimPlayback(audio);
    await audio.play();
}

export function resetMonoPlaybackForTests(): void {
    sharedContext = null;
}
